import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, Star } from "lucide-react";
import { KICKSTARTER_URL } from "@/lib/links";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden pb-[0.08em] ${className}`}>
      <motion.span
        className="block will-change-transform"
        initial={{ y: "112%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function SpinBadge() {
  return (
    <div className="absolute -left-8 -top-10 z-20 h-24 w-24 md:-left-12 md:-top-12 md:h-32 md:w-32">
      <div className="h-full w-full animate-spin-slow">
        <svg viewBox="0 0 100 100" className="h-full w-full drop-shadow-[3px_3px_0_rgba(46,49,146,1)]">
          <circle cx="50" cy="50" r="47" fill="#F2E85C" stroke="#2E3192" strokeWidth="3" />
          <defs>
            <path id="badge-circle" d="M 50,50 m -33,0 a 33,33 0 1,1 66,0 a 33,33 0 1,1 -66,0" fill="none" />
          </defs>
          <text fill="#2E3192" fontSize="8.5" fontWeight="700" letterSpacing="1.5" fontFamily="Archivo, sans-serif">
            <textPath href="#badge-circle">KICKSTARTER · COMING SOON ·</textPath>
          </text>
        </svg>
      </div>
      <Star className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 fill-punch text-punch" />
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yCover = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yShapes = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 140, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 140, damping: 16 });

  const handleMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const handleLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-sky">
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ background: "radial-gradient(55% 45% at 74% 36%, rgba(242,232,92,0.55) 0%, rgba(242,232,92,0) 70%)" }}
      />

      <motion.div aria-hidden="true" style={{ y: yShapes }} className="pointer-events-none absolute inset-0">
        <div className="absolute left-[5%] top-[16%] h-16 w-16 animate-float rounded-full border-[10px] border-punch md:h-24 md:w-24 md:border-[14px]" />
        <div className="absolute right-[34%] top-[6%] hidden animate-float lg:block" style={{ animationDelay: "-2.5s" }}>
          <svg width="90" height="90" viewBox="0 0 90 90" aria-hidden="true">
            <path d="M45 0 L53 37 L90 45 L53 53 L45 90 L37 53 L0 45 L37 37 Z" fill="#00A651" />
          </svg>
        </div>
        <div
          className="absolute bottom-[5%] left-[40%] hidden h-10 w-24 animate-float rounded-full border-4 border-navy bg-banana md:block"
          style={{ animationDelay: "-4.5s" }}
        />
      </motion.div>

      <header className="relative z-30 mx-auto flex w-full max-w-[1440px] items-center justify-between px-6 pt-6 md:px-12">
        <a href="#top" data-testid="brand-link" className="font-display text-2xl font-extrabold uppercase tracking-tight text-navy">
          Bookata<span className="text-punch">*</span>
        </a>
        <p className="hidden text-[11px] font-bold uppercase tracking-[0.35em] text-navy/70 md:block">
          2027 Calendario Gastronomico
        </p>
        <a
          href={KICKSTARTER_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="nav-kickstarter-button"
          className="rounded-full border-2 border-navy bg-navy px-5 py-2 text-sm font-bold uppercase tracking-wider text-banana transition-colors duration-300 hover:bg-punch hover:text-navy"
        >
          Back it on Kickstarter
        </a>
      </header>

      <div className="relative z-10 mx-auto grid w-full max-w-[1440px] items-center gap-14 px-6 pb-32 pt-12 md:px-12 lg:grid-cols-[1.2fr_1fr] lg:pt-6">
        <div>
          <MaskedLine delay={0.05}>
            <span className="text-[11px] font-bold uppercase tracking-[0.4em] text-leaf md:text-xs">
              Bookata — a 2027 wall calendar
            </span>
          </MaskedLine>
          <h1
            data-testid="hero-title"
            className="mt-6 font-display text-[clamp(3.1rem,9.5vw,8.25rem)] font-extrabold uppercase leading-[0.88] tracking-tight text-navy"
          >
            <MaskedLine delay={0.15}>When, where</MaskedLine>
            <MaskedLine delay={0.27}>
              <span className="text-punch">&amp; what</span>
            </MaskedLine>
            <MaskedLine delay={0.39}>
              to <span className="text-leaf">eat</span>
              <span className="text-punch">.</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.6} className="mt-7">
            <span data-testid="hero-tagline" className="font-serif text-xl italic text-navy/90 md:text-2xl">
              The right dish, the perfect place, the proper time.
            </span>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={KICKSTARTER_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-kickstarter"
              className="rounded-full border-2 border-navy bg-punch px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-navy shadow-[5px_5px_0_#2E3192] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[8px_8px_0_#2E3192]"
            >
              Back it on Kickstarter
            </a>
            <a
              href="#gallery"
              data-testid="hero-cta-gallery"
              className="group inline-flex items-center gap-2 rounded-full border-2 border-navy px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-navy transition-colors duration-300 hover:bg-navy hover:text-banana"
            >
              Peek the months
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
          </motion.div>
        </div>

        <motion.div style={{ y: yCover }} className="relative flex justify-center [perspective:1400px] lg:justify-end">
          <motion.div
            data-testid="hero-cover"
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, y: 80, rotate: 6 }}
            animate={{ opacity: 1, y: 0, rotate: 2 }}
            transition={{ delay: 0.45, duration: 1, ease: EASE }}
            className="relative"
          >
            <div aria-hidden="true" className="absolute -bottom-5 -right-5 h-full w-full border-4 border-punch" />
            <div className="relative border-4 border-navy bg-paper p-2.5 shadow-[12px_12px_0_#2E3192]">
              <img
                src="/images/cover.jpg"
                alt="Cover of the Bookata 2027 Gastronomico calendar — When, Where & What to Eat"
                data-testid="hero-cover-image"
                className="aspect-[1224/1584] w-[280px] object-cover md:w-[340px] xl:w-[380px]"
              />
            </div>
            <SpinBadge />
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#concept"
        data-testid="hero-scroll-hint"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        className="absolute bottom-24 left-1/2 z-20 hidden -translate-x-1/2 items-center gap-2 text-[11px] font-bold uppercase tracking-[0.35em] text-navy/70 transition-colors hover:text-navy lg:flex"
      >
        Scroll <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.a>
    </section>
  );
}
